import { Box, Button, DialogActions, Grid } from "@mui/material";
import QueryBuilder from "../../components/QueryBuilder/Index";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import { useRef, useState } from "react";
import { useRuleResourceContext } from "../../contexts/RuleResourceContext";
import * as Yup from "yup";
import {
  extractColumnNamesFromQuery,
  extractDollarPrefixedColumns,
} from "../../services/utils";
import { useToast } from "../../services/utils";
import EnhancedQueryDialog from "./EnhancedQueryDialog";

interface SqlQueryDialogProps {
  showQryModal: boolean;
  setShowQryModal: React.Dispatch<React.SetStateAction<boolean>>;
  onSaveQuery: () => void;
  setHasVariableChanges?: React.Dispatch<React.SetStateAction<boolean>>;
}

const SqlQueryDialog = ({
  showQryModal,
  setShowQryModal,
  onSaveQuery,
  setHasVariableChanges,
}: SqlQueryDialogProps) => {
  const {
    setQueryBuilderTempValue,
    queryBuilderTempValue,
    setTempGlobalVariables,
    setGlobalVariables,
    tempGlobalVariables,
    availColumns,
    aggregatedAvailColumns,
    setShowAggregatedColumns,
    showAggregatedColumns,
    updateVariableUsageFromTempGlobalVariables,
    updateViewInlineVariables,
    seletedResourceId,
  } = useRuleResourceContext();
  const inputRefs = useRef<{ [key: string]: HTMLInputElement | null }>({});
  // Register input refs dynamically
  const setInputRef = (key: string) => (el: HTMLInputElement | null) => {
    inputRefs.current[key] = el;
  };
  const { showToast } = useToast();

  const [error, setError] = useState<{ [key: string]: string }>({});

  const handleChangeQuery = (qry: any, columns: any) => {
    // setQueryBuilderTempValue(qry?.trim() ?? "");
  };
  const handleSaveQuery = async () => {
    try {
      // Extract flat columns for validation (backward compatibility)
      let availColumnsToBeUsed: any = [];
      if (showAggregatedColumns) {
        availColumnsToBeUsed = aggregatedAvailColumns;
      } else {
        availColumnsToBeUsed = Array.isArray(availColumns)
          ? availColumns
          : (availColumns as any)?.flatColumns || [];
      }

      const usedColumn = extractDollarPrefixedColumns(queryBuilderTempValue);
      const isValid = usedColumn.every((columnName: any) =>
        availColumnsToBeUsed.includes(columnName)
      );
      if (!isValid) {
        showToast(
          "The values enclosed in square brackets are reserved; only the available variables within the square brackets may be used.",
          "warning"
        );
        return;
      }
      if (queryBuilderTempValue) {
        const queryVariablesSchema: any = {};
        Object.keys(tempGlobalVariables).map((key: any) => {
          if (tempGlobalVariables[key] === "") {
            queryVariablesSchema[key] = Yup.string().required(
              `Please enter ${key}`
            );
          } else {
            return {
              name: key,
              value: tempGlobalVariables[key],
            };
          }
        });

        await Yup.object()
          .shape({ ...queryVariablesSchema })
          .validate(tempGlobalVariables, {
            abortEarly: false,
          });

        setError((prevError) => ({
          ...prevError,
          query: "",
        }));
      } else {
        setError((prevError) => ({
          ...prevError,
          query: "Please enter query",
        }));
        return;
      }
      //the Function for updating the used variables in ValidateResource
      updateVariableUsageFromTempGlobalVariables();

      //the Function for updating the used variables in ViewInlineVariables (Rule Execution)
      updateViewInlineVariables(seletedResourceId);

      if (setHasVariableChanges) {
        setGlobalVariables((prev: any) => {
          let hasChanged = false;

          const updated = Object.keys(tempGlobalVariables).reduce(
            (acc, key) => {
              if (prev[key] !== tempGlobalVariables[key]) {
                hasChanged = true;
              }
              acc[key] = tempGlobalVariables[key];
              return acc;
            },
            { ...prev }
          );

          if (hasChanged) {
            setHasVariableChanges(true);
          }

          return updated;
        });
      } else {
        setGlobalVariables((prev: any) => ({
          ...Object.keys(tempGlobalVariables).reduce(
            (acc, key) => ({ ...acc, [key]: tempGlobalVariables[key] }),
            prev
          ),
        }));
      }

      setTempGlobalVariables({});
      onSaveQuery();
      setQueryBuilderTempValue("");
      setShowQryModal(false);
    } catch (err: any) {
      err.inner.forEach((error: any) => {
        setError((prevError) => ({
          ...prevError,
          [error.path]: error.message,
        }));
      });
      // Focus the first input with an error
      if (err.inner.length > 0) {
        const firstErrorPath = err.inner[0].path;
        const inputToFocus = inputRefs.current[firstErrorPath];
        if (inputToFocus) {
          inputToFocus.focus();
        }
      }
      return;
    }
    setShowAggregatedColumns(false);
  };
  const handleCloseQueryModal = () => {
    setShowQryModal(false);
    setQueryBuilderTempValue("");
    setTempGlobalVariables({});
    setError({});
    setShowAggregatedColumns(false);
  };
  // Check if there are unsaved changes
  const hasUnsavedChanges =
    (queryBuilderTempValue?.length > 0 &&
      Boolean(queryBuilderTempValue.trim())) ||
    Object.keys(tempGlobalVariables).length > 0;

  return (
    <>
      <EnhancedQueryDialog
        fullWidth
        maxWidth="xl"
        open={showQryModal}
        onClose={handleCloseQueryModal}
        className="main-dailog main-query-builder-dialog"
        hasUnsavedChanges={hasUnsavedChanges}
        confirmationTitle="Unsaved Query Changes"
        confirmationContent="You have unsaved query changes that will be lost. Are you sure you want to close?"
      >
        <Box sx={{ padding: 3 }}>
          <Grid container className="dailog-header">
            <label className="label-text pt-13">Add Query</label>
            <div className="close-icon" onClick={handleCloseQueryModal}></div>
          </Grid>
          <Box className="dailog-body pb-24">
            <Box>
              <Grid container rowSpacing={2.5} columnSpacing={6.25}>
                <QueryBuilder
                  handleChange={handleChangeQuery}
                  error={error}
                  setErrors={setError}
                  setInputRef={setInputRef}
                />
              </Grid>
            </Box>
          </Box>
          <DialogActions className="dailog-footer">
            <Button
              color="secondary"
              variant="contained"
              onClick={handleSaveQuery}
              className="btn-orange"
            >
              <SaveOutlinedIcon /> &nbsp; Save
            </Button>
          </DialogActions>
        </Box>
      </EnhancedQueryDialog>
    </>
  );
};

export default SqlQueryDialog;
